<script setup>
// Imports
import { QDialog } from 'quasar'
import { updatePwaApp } from 'src/modules/pwa-update.js'

// Options
defineOptions({ name: 'PwaUpdateNotice' })

// Stores
const updates = usePwaUpdateStore()

// Constants
const { t } = useI18n()

// Computed
// Unsupported clients use the same card inside a modal that cannot be dismissed.
const containerProps = computed(() =>
  updates.unsupported
    ? { modelValue: true, persistent: true, noShake: true, 'aria-labelledby': 'pwa-update-title', 'aria-describedby': 'pwa-update-message' }
    : {},
)
const titleKey = computed(() => (updates.unsupported || updates.loadFailed ? 'Shared.Globals.PwaUpdateRequired' : 'Shared.Globals.PwaUpdateAvailable'))
const messageKey = computed(() =>
  updates.unsupported ? 'Shared.Globals.PwaUnsupported' : updates.loadFailed ? 'Shared.Globals.PwaLoadFailed' : 'Shared.Globals.PwaUpdateMessage',
)
</script>

<template>
  <component
    :is="updates.unsupported ? QDialog : 'div'"
    v-if="updates.unsupported || updates.available || updates.loadFailed || updates.error"
    v-bind="containerProps"
    :class="updates.unsupported ? 'pwa-update-dialog' : 'pwa-update-notice fixed-bottom-right q-ma-sm'"
  >
    <q-card
      v-if="!updates.dismissed || updates.unsupported || updates.loadFailed || updates.error"
      class="pwa-update-card"
      :role="updates.unsupported ? undefined : 'status'"
    >
      <q-card-section class="pwa-update-hero text-center q-px-lg q-pt-lg q-pb-lg">
        <div class="pwa-update-icon q-mx-auto q-mb-md" aria-hidden="true">
          <q-icon name="sym_o_system_update_alt" size="32px" />
        </div>
        <h2 id="pwa-update-title" class="text-h6 text-weight-bold q-my-none">{{ t(titleKey) }}</h2>
        <p id="pwa-update-message" class="text-body2 text-grey-8 q-mt-md q-mb-none">{{ t(messageKey) }}</p>
      </q-card-section>
      <q-card-section v-if="(updates.loadFailed && !updates.unsupported) || updates.error" class="q-px-lg q-pt-none q-pb-lg">
        <div class="pwa-update-hint row items-start no-wrap q-pa-md rounded-borders">
          <q-icon name="sym_o_info" color="primary" size="22px" class="q-mr-sm" aria-hidden="true" />
          <div class="text-body2 text-grey-8">
            <div v-if="updates.loadFailed && !updates.unsupported">{{ t('Shared.Globals.PwaSaveFirst') }}</div>
            <div v-if="updates.error" class="text-negative" role="alert">{{ t('Shared.Globals.PwaUpdateFailed') }}</div>
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions class="q-pa-md q-px-lg" align="right">
        <q-btn
          v-if="!updates.unsupported && !updates.loadFailed"
          flat
          no-caps
          color="grey-8"
          :label="t('Shared.Globals.Later')"
          :disable="updates.updating"
          @click="updates.dismissed = true"
        />
        <q-btn
          color="primary"
          unelevated
          no-caps
          icon="sym_o_system_update_alt"
          :label="t(updates.error ? 'Shared.Globals.Retry' : 'Shared.Globals.PwaUpdateNow')"
          :loading="updates.updating"
          :disable="updates.updating"
          @click="updatePwaApp"
        />
      </q-card-actions>
    </q-card>
    <q-btn
      v-else
      unelevated
      no-caps
      color="primary"
      icon="sym_o_system_update_alt"
      :label="t('Shared.Globals.PwaUpdateNow')"
      @click="updates.dismissed = false"
    />
  </component>
</template>

<style scoped>
:global(.pwa-update-dialog) {
  z-index: 10000;
}

.pwa-update-notice {
  z-index: 10000;
  max-width: min(420px, calc(100vw - 16px));
}

.pwa-update-card {
  width: min(400px, calc(100vw - 16px));
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(21, 37, 76, 0.2);
}

.pwa-update-hero {
  background: radial-gradient(circle at top, rgba(var(--q-primary-rgb, 234, 130, 21), 0.13), transparent 62%), #fff;
}

.pwa-update-icon {
  display: grid;
  width: 64px;
  height: 64px;
  color: var(--q-primary);
  background: rgba(var(--q-primary-rgb, 234, 130, 21), 0.12);
  border: 1px solid rgba(var(--q-primary-rgb, 234, 130, 21), 0.22);
  border-radius: 50%;
  place-items: center;
}

.pwa-update-hint {
  background: #f5f7fb;
  border: 1px solid #e4e9f2;
}
</style>
